import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../components/Button/Button';
import { useFocusTrap } from '../components/useFocusTrap';
import type { ProjectKind } from '../data/types';
import './CreateProjectDialog.css';

const MAX_NAME_LENGTH = 60;

const KIND_OPTIONS: { value: ProjectKind; label: string; helper: string }[] = [
  {
    value: 'image',
    label: 'Camera',
    helper: 'Teach it with pictures from your camera or files.',
  },
  {
    value: 'audio',
    label: 'Mic',
    helper: 'Teach it with one-second sounds from your microphone.',
  },
  {
    value: 'pose',
    label: 'Pose',
    helper: 'Teach it body poses (arms up, sitting, dancing) from your camera.',
  },
];

export interface CreateProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: { name: string; kind: ProjectKind }) => void | Promise<void>;
}

export function CreateProjectDialog({
  isOpen,
  onClose,
  onCreate,
}: CreateProjectDialogProps): JSX.Element | null {
  const titleId = useId();
  const nameId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ProjectKind>('image');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setKind('image');
      setSubmitting(false);
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useFocusTrap(isOpen, dialogRef);

  if (!isOpen) return null;

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onCreate({ name: trimmed, kind });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bl-dialog__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bl-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <h2 id={titleId} className="bl-dialog__title">
          New project
        </h2>
        <form className="bl-dialog__form" onSubmit={handleSubmit}>
          <label className="bl-dialog__label" htmlFor={nameId}>
            What do you want to call it?
          </label>
          <input
            id={nameId}
            ref={nameInputRef}
            className="bl-dialog__input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={MAX_NAME_LENGTH}
            required
            data-autofocus
          />

          <fieldset className="bl-dialog__fieldset">
            <legend className="bl-dialog__legend">How will it learn?</legend>
            <div className="bl-dialog__radios">
              {KIND_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`bl-dialog__radio ${
                    kind === option.value ? 'bl-dialog__radio--selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="kind"
                    value={option.value}
                    checked={kind === option.value}
                    onChange={() => setKind(option.value)}
                  />
                  <span className="bl-dialog__radio-label">{option.label}</span>
                  <span className="bl-dialog__radio-helper">{option.helper}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="bl-dialog__actions">
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
